import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/app/project-dialogs';
import { Spinner, StatusPill } from '@/components/app/misc';
import { IconAction } from '@/components/leads/icon-action';
import { Button } from '@/components/ui/button';
/**
 * Histórico de exportações CSV geradas em fila (funnel.requests.export.history).
 * Atualiza a cada 5s enquanto houver arquivo "gerando" e avisa quando termina.
 */
export function useExportHistory(initial, historyUrl) {
    const [files, setFiles] = React.useState(initial);
    const [open, setOpen] = React.useState(false);
    const last = React.useRef(Object.fromEntries(initial.map((f) => [f.id, f.status])));
    const timer = React.useRef(null);
    const refresh = React.useCallback(() => {
        axios
            .get(historyUrl)
            .then(({ data: res }) => {
            if (!res || !res.success)
                return;
            const next = res.data || [];
            next.forEach((f) => {
                const prev = last.current[f.id];
                if (prev === 'processing' && f.status === 'completed')
                    toast.success('Arquivo de leads gerado! Já pode ser baixado.');
                else if (prev === 'processing' && f.status === 'failed')
                    toast.error('Falha ao gerar o arquivo de leads.');
            });
            last.current = Object.fromEntries(next.map((f) => [f.id, f.status]));
            setFiles(next);
        })
            .catch(() => { });
    }, [historyUrl]);
    const processing = files.filter((f) => f.status === 'processing').length;
    React.useEffect(() => {
        if (timer.current)
            window.clearTimeout(timer.current);
        if (processing > 0)
            timer.current = window.setTimeout(refresh, 5000);
        return () => {
            if (timer.current)
                window.clearTimeout(timer.current);
        };
    }, [files, processing, refresh]);
    return { files, open, setOpen, refresh, processing };
}
export function ExportHistoryPanel({ files, refresh, threshold, expiresDays, maxHistory, }) {
    const [toDelete, setToDelete] = React.useState(null);
    const remove = (file) => {
        axios
            .delete(file.destroy_url, { headers: { Accept: 'application/json' } })
            .then(({ data }) => {
            if (data && data.success) {
                toast.success('Arquivo removido do histórico.');
                refresh();
            }
            else
                toast.error('Não foi possível excluir o arquivo.');
        })
            .catch(() => toast.error('Não foi possível excluir o arquivo.'));
    };
    return (_jsxs("div", { className: "grid gap-3 rounded-xl border p-4", children: [_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [_jsxs("h3", { className: "flex items-center gap-2 text-sm font-semibold", children: [_jsx(FileSpreadsheet, { className: "h-4 w-4 text-primary" }), "Hist\u00F3rico de exporta\u00E7\u00F5es"] }), _jsxs("span", { className: "text-xs text-muted-foreground", children: ["Dispon\u00EDvel por ", expiresDays, " dias \u00B7 m\u00E1ximo ", maxHistory, " arquivos"] })] }), _jsxs("p", { className: "text-xs text-muted-foreground", children: ["Exporta\u00E7\u00F5es com mais de ", threshold.toLocaleString('pt-BR'), " leads s\u00E3o geradas em segundo plano e listadas aqui."] }), files.length === 0 ? (_jsx("p", { className: "py-4 text-center text-[13px] text-muted-foreground", children: "Nenhuma exporta\u00E7\u00E3o gerada ainda." })) : (_jsx("ul", { className: "grid gap-2", children: files.map((file) => (_jsxs("li", { className: "flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2.5", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "truncate text-[13px] font-semibold", title: file.name, children: file.name }), _jsxs("p", { className: "text-xs text-muted-foreground", children: [(file.total_leads || 0).toLocaleString('pt-BR'), " leads \u00B7 Gerado em ", file.created_at || '-', file.expires_at ? ` · Expira em ${file.expires_at}` : ''] })] }), _jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [file.status === 'processing' ? (_jsxs(StatusPill, { tone: "warning", children: [_jsx(Spinner, { className: "h-3 w-3" }), " Gerando..."] })) : file.status === 'failed' ? (_jsx(StatusPill, { tone: "danger", children: "Falhou" })) : file.downloadable ? (_jsx(Button, { size: "sm", asChild: true, children: _jsxs("a", { href: file.download_url, children: [_jsx(Download, {}), "Baixar"] }) })) : (_jsx(StatusPill, { tone: "neutral", children: "Indispon\u00EDvel" })), file.status !== 'processing' && file.destroy_url ? (_jsx(IconAction, { label: "Excluir do hist\u00F3rico", children: _jsx(Button, { variant: "outline", size: "icon", className: "h-8 w-8 text-destructive hover:text-destructive", "aria-label": `Excluir ${file.name} do histórico`, onClick: () => setToDelete(file), children: _jsx(Trash2, {}) }) })) : null] })] }, file.id))) })), _jsx(ConfirmDialog, { open: !!toDelete, title: "Excluir arquivo?", description: "O arquivo ser\u00E1 removido permanentemente do hist\u00F3rico.", confirmLabel: "Sim, excluir", onClose: () => setToDelete(null), onConfirm: () => {
                    if (toDelete)
                        remove(toDelete);
                    setToDelete(null);
                } })] }));
}
